import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/api/client';
import { API_VIDEO_MEETINGS } from '@/api/endpoints';
import {
  VIDEO_MEETING_QUERY_KEY,
  VIDEO_MEETINGS_QUERY_KEY,
} from '@/api/queryKeys';
import { useRefreshProfiles } from '@/hooks/useRefreshProfiles';
import { ErrorMessage, logRequestError } from '@/utils/errors';
import { joinPath } from '@/utils/global';

interface Params {
  meetingId: string;
  isAttending: boolean;
}

export const useRespondToVideoMeeting = (onError: () => void) => {
  const queryClient = useQueryClient();
  const refreshProfiles = useRefreshProfiles();

  const {
    mutate: respond,
    isPending,
    variables,
  } = useMutation({
    mutationFn: ({ meetingId, isAttending }: Params) =>
      apiClient.patch(
        `${joinPath(API_VIDEO_MEETINGS, meetingId)}/current-user`,
        { isAttending },
      ),
    onSuccess: async (_, { meetingId }) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: VIDEO_MEETINGS_QUERY_KEY }),
        queryClient.invalidateQueries({
          queryKey: [...VIDEO_MEETING_QUERY_KEY, meetingId],
        }),
        refreshProfiles(),
      ]);
    },
    onError: (error) => {
      logRequestError(ErrorMessage.RespondToVideoMeeting, error);
      onError();
    },
  });

  return {
    respondingId: isPending ? variables.meetingId : null,
    approve: (meetingId: string) => respond({ meetingId, isAttending: true }),
    decline: (meetingId: string) => respond({ meetingId, isAttending: false }),
  };
};
